import React, { useEffect, useState } from "react";
import YhInput from "../../../../../../ui/YhInput";
import YhButton from "../../../../../../ui/YhButton";
import YhTable, {
  YhTableColumn,
  YhTableCellParams,
} from "../../../../../../ui/YhTable";
import {
  getDeveloperTreeSerch,
  getDeveloperTree,
  addDeveloper,
} from "../../../../../Project/components/stepper/StepperService";
import { applyLicenseKey } from "../../../../../../utils/License";
import AddDeveloper from "./AddDeveloper";

const SearchSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={`shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
  >
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const transformProjectData = (data: any[]) =>
  data.flatMap((developer) => [
    {
      _id: developer._id,
      developer_name: developer.developer_name,
      countryId: developer.countryId || "",
      countryName: developer.countryName || "",
      path: [developer.developer_name],
      isParent: true,
      isTop: developer.isTop || 0,
      status: developer.status ?? 0,
      image: developer.image || "",
      childCount: (developer.projects || []).length,
    },
    ...(developer.projects || []).map((project: any) => ({
      _id: project._id,
      developerId: developer._id,
      developer_name: developer.developer_name,
      project_name: project.project_name,
      path: [developer.developer_name, project.project_name],
      project_uid: project.project_uid,
      updatedAt: project.updatedAt,
      parentId: developer._id,
      isParent: false,
    })),
  ]);

const AddProjectMatadata = () => {
  const [projectData, setProjectData] = useState<any[]>([]);
  const [metaData, setMetaData] = useState<any>({ total: 0 });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });
  const [openEdit, setOpenEdit] = useState(false);
  const [developerData, setDeveloperData] = useState<any>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    applyLicenseKey();
  }, []);

  useEffect(() => {
    getAllProjectData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, paginationModel.page, paginationModel.pageSize, openEdit]);

  const getAllProjectData = async () => {
    try {
      setLoading(true);
      const payload = {
        search,
        pageSize: paginationModel.pageSize,
        pageNo: paginationModel.page,
      };
      const result = await getDeveloperTreeSerch(payload);
      const rows = transformProjectData(result?.data || []);
      setMetaData(result?.metadata?.[0] || result?.metaData || { total: 0 });
      setProjectData(rows);

      setExpanded((prev) => {
        const next: Record<string, boolean> = {};
        rows.forEach((row) => {
          if (row.isParent && row.childCount > 0) {
            next[row._id] = prev[row._id] ?? true;
          }
        });
        return next;
      });
    } catch (error) {
      console.error("Error fetching project meta:", error);
    } finally {
      setLoading(false);
    }
  };

  const visibleRows = projectData.filter((row) => {
    if (row.isParent) return true;
    return !!expanded[row.parentId];
  });

  const rowDataHandle = async (row: any) => {
    try {
      const response = await getDeveloperTree(row._id);
      const data = response?.data?.[0];
      if (data) {
        setDeveloperData({
          ...data,
          isProject: data.project_name ? 1 : 0,
        });
      }
    } catch (error) {
      console.error("Error loading row data:", error);
    }
  };

  const closeDrawer = () => {
    setOpenEdit(false);
    setDeveloperData({});
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const columns: YhTableColumn[] = [
    {
      headerName: "Developer / Project",
      field: "developer_name",
      minWidth: 360,
      flex: 1,
      renderCell: (params: YhTableCellParams) => {
        const row = params.row;
        if (row.isParent) {
          const isOpen = !!expanded[row._id];
          const hasChildren = (row.childCount || 0) > 0;
          return (
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={`flex h-6 w-6 items-center justify-center rounded text-[#6B7280] ${
                  hasChildren ? "hover:bg-[#F3F4F6]" : "invisible"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasChildren) toggleExpand(row._id);
                }}
                aria-label={isOpen ? "Collapse" : "Expand"}
              >
                <Chevron open={isOpen} />
              </button>
              <div>
                <p className="text-sm font-semibold capitalize text-[#111827]">
                  {row.developer_name || "—"}
                </p>
                {row.countryName ? (
                  <p className="text-xs text-[#9CA3AF]">{row.countryName}</p>
                ) : null}
              </div>
            </div>
          );
        }
        return (
          <div className="ml-10 pl-2">
            <p className="text-sm font-medium text-[#374151]">
              {row.project_name || "—"}
            </p>
            {row.project_uid ? (
              <p className="text-xs text-[#9CA3AF]">{row.project_uid}</p>
            ) : null}
          </div>
        );
      },
    },
    {
      headerName: "Website",
      field: "status",
      minWidth: 120,
      renderCell: (params: YhTableCellParams) => {
        if (!params.row.isParent) return null;
        const checked = params.row.status === 1;
        const hasImage = Boolean(String(params.row.image || "").trim());
        return (
          <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
            <label className="relative inline-flex h-6 w-11 cursor-pointer items-center">
              <input
                type="checkbox"
                className="peer sr-only"
                checked={checked}
                disabled={!hasImage && !checked}
                title={
                  !hasImage && !checked
                    ? "Upload a developer logo before activating on website"
                    : checked
                      ? "Active on yhataw.com"
                      : "Inactive on yhataw.com"
                }
                onChange={async (e) => {
                  const updatedValue = e.target.checked ? 1 : 0;
                  const prev = params.row.status;
                  setProjectData((rows) =>
                    rows.map((row) =>
                      row._id === params.row._id
                        ? { ...row, status: updatedValue }
                        : row
                    )
                  );
                  try {
                    await addDeveloper({
                      _id: params.row._id,
                      status: updatedValue,
                    });
                  } catch {
                    setProjectData((rows) =>
                      rows.map((row) =>
                        row._id === params.row._id
                          ? { ...row, status: prev }
                          : row
                      )
                    );
                  }
                }}
              />
              <span className="absolute inset-0 rounded-full bg-[#D1D5DB] transition peer-checked:bg-[#A02448] peer-disabled:opacity-40" />
              <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
            </label>
            <span
              className={`text-[10px] font-semibold uppercase ${
                checked ? "text-[#047857]" : "text-[#9CA3AF]"
              }`}
            >
              {checked ? "Active" : "Inactive"}
            </span>
          </div>
        );
      },
    },
    {
      headerName: "Top Developer",
      field: "isTop",
      minWidth: 140,
      renderCell: (params: YhTableCellParams) => {
        if (!params.row.isParent) return null;
        const checked =
          params.row.isTop === 1 || params.row.isTop === true;
        return (
          <label
            className="relative inline-flex h-6 w-11 cursor-pointer items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              className="peer sr-only"
              checked={!!checked}
              onChange={async (e) => {
                const updatedValue = e.target.checked ? 1 : 0;
                const prev = params.row.isTop;
                setProjectData((rows) =>
                  rows.map((row) =>
                    row._id === params.row._id
                      ? { ...row, isTop: updatedValue }
                      : row
                  )
                );
                try {
                  await addDeveloper({
                    _id: params.row._id,
                    isTop: updatedValue,
                  });
                } catch {
                  setProjectData((rows) =>
                    rows.map((row) =>
                      row._id === params.row._id
                        ? { ...row, isTop: prev }
                        : row
                    )
                  );
                }
              }}
            />
            <span className="absolute inset-0 rounded-full bg-[#D1D5DB] transition peer-checked:bg-[#A02448]" />
            <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
          </label>
        );
      },
    },
    {
      headerName: "Action",
      field: "action",
      minWidth: 90,
      renderCell: (params: YhTableCellParams) => (
        <button
          type="button"
          data-action-button="true"
          className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F3F4F6]"
          onClick={async (e) => {
            e.stopPropagation();
            await rowDataHandle(params.row);
            setOpenEdit(true);
          }}
          aria-label="Edit"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
          </svg>
        </button>
      ),
    },
  ];

  return (
    <div className="flex h-[calc(100vh-68px)] max-h-[calc(100vh-68px)] flex-col overflow-hidden bg-white p-4 sm:p-5 lg:p-6">
      <div className="mb-4 flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#1E1B4B] sm:text-3xl">
            Projects Meta Data
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            Define project meta data. Website = Active shows developer on yhataw.com (logo required).
          </p>
        </div>
        <YhButton
          variant="primary"
          size="sm"
          onClick={() => {
            setDeveloperData({});
            setOpenEdit(true);
          }}
        >
          + Create
        </YhButton>
      </div>

      <div className="mb-3 shrink-0 sm:max-w-[420px]">
        <YhInput
          placeholder="Search developers or projects…"
          value={search}
          type="search"
          onChange={(e) => {
            setSearch(e.target.value);
            setPaginationModel((p) => ({ ...p, page: 0 }));
          }}
          startAdornment={<SearchSvg />}
        />
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden">
        <YhTable
          columns={columns}
          rows={visibleRows}
          getRowId={(row) => row._id}
          loading={loading}
          emptyMessage="No project meta found"
          className="h-full"
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          totalRows={metaData?.total || 0}
          pageSizeOptions={[10, 25, 50, 100, 200]}
          onPageChange={(p) =>
            setPaginationModel((prev) => ({ ...prev, page: p }))
          }
          onPageSizeChange={(s) =>
            setPaginationModel({ pageSize: s, page: 0 })
          }
        />
      </div>

      {openEdit ? (
        <AddDeveloper
          developerData={developerData}
          setDeveloperData={setDeveloperData}
          closeDrawer={closeDrawer}
        />
      ) : null}
    </div>
  );
};

export default AddProjectMatadata;
